import { Injectable, inject } from '@angular/core';
import { HotkeysService } from '../../../../../core/services/hotkeys.service';
import { mapKey } from '../models/cmdk-keymap';
import type { CmdkIntent, CmdkKeyContext } from '../models/cmdk-keymap';

/**
 * RESPONSIBILITY: owns the palette's keyboard layer while it is open — the
 * `HotkeysService` suspension lease, the capture-phase listener, and the
 * translation of a keystroke into a `CmdkIntent`.
 *
 * It does NOT decide what an intent means. `mapKey` (pure, in
 * `models/cmdk-keymap.ts`) decides which intent a keystroke is; the palette
 * component decides what to do with it. This service is only the plumbing
 * between them, which is what keeps the interesting parts testable.
 *
 * USED BY: CmdkComponent (attach/detach on open/close).
 *
 * AI CONTEXT — why the palette cannot just use HotkeysService
 *
 * Three independent reasons, each on its own sufficient:
 *
 *  1. `handleKeydown` returns immediately unless ⌘/Ctrl is held
 *     (`hotkeys.service.ts:149`) and its map is keyed `mod+<key>`. Bare keys,
 *     `⇧↵` and `⌥Space` cannot be expressed. Three bare-key bindings are
 *     already stored-and-never-dispatched at `schedule-commands.service.ts:129`.
 *  2. It listens on `document` at CAPTURE phase and `stopPropagation()`s on
 *     match, so a bubble-phase handler inside the palette can never take ⌘K,
 *     ⌘↵ or ⌘1–9 back from it.
 *  3. ⌘K / ⌘P / ⌘/ are registered `global: true`, meaning they fire even while
 *     focus is in an input — including the palette's own input. Without a
 *     lease, ⌘K inside the palette re-toggles the shell signal underneath us.
 *
 * So: take `hotkeys.suspend()` for the open lifetime, bind our own listener at
 * capture phase on the palette root, and re-implement ⌘K-to-close locally.
 *
 * The listener is on the palette ELEMENT, not on `document`. While the palette
 * is open focus is trapped inside it, so every keystroke passes through — and
 * scoping to the element means a stray global listener can never survive a
 * missed teardown.
 */

/** Supplies the live context and receives the resolved intent. */
export interface CmdkKeymapHost {
  /** Read the current context. Called once per keystroke. */
  context(): CmdkKeyContext;
  /** Act on a resolved intent. */
  dispatch(intent: CmdkIntent): void;
  /** Notifies the host that ⌘ (or Ctrl) is held, for the quick-open badges. */
  setModifierHeld(held: boolean): void;
}

@Injectable({ providedIn: 'root' })
export class CmdkKeymapService {
  private readonly hotkeys = inject(HotkeysService);

  private releaseSuspension: (() => void) | null = null;
  private detachListeners: (() => void) | null = null;

  /**
   * Bind the palette's keyboard layer to an element. Returns a disposer; the
   * caller must invoke it, and calling `attach` twice without detaching is a
   * no-op on the second call so a double-open cannot leak a lease.
   */
  attach(element: HTMLElement, host: CmdkKeymapHost): () => void {
    if (this.detachListeners) return this.detachListeners;

    this.releaseSuspension = this.hotkeys.suspend();

    const onKeyDown = (event: KeyboardEvent): void => {
      // Track the modifier for the ⌘1–9 badges before anything can bail out.
      if (event.key === 'Meta' || event.key === 'Control') host.setModifierHeld(true);

      const intent = mapKey(event, host.context());
      if (!intent) return;

      // A returned intent means the palette has claimed the keystroke.
      // Letting the browser also act is how ⌘1 both quick-opens a result and
      // switches browser tab.
      event.preventDefault();
      event.stopPropagation();
      host.dispatch(intent);
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === 'Meta' || event.key === 'Control') host.setModifierHeld(false);
    };

    // A window blur while ⌘ is down never produces a keyup, so the badges
    // would stay lit until the next press. This is the fix for that.
    const onWindowBlur = (): void => host.setModifierHeld(false);

    element.addEventListener('keydown', onKeyDown, true);
    element.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onWindowBlur);

    this.detachListeners = () => {
      element.removeEventListener('keydown', onKeyDown, true);
      element.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onWindowBlur);
      this.releaseSuspension?.();
      this.releaseSuspension = null;
      this.detachListeners = null;
    };

    return this.detachListeners;
  }

  /** Idempotent teardown, for a destroy path that may race the disposer. */
  detach(): void {
    this.detachListeners?.();
  }

  /** ⌘ on macOS, Ctrl+ elsewhere — for hint rows and badges. */
  get modifierSymbol(): string {
    return this.hotkeys.modifierSymbol;
  }
}
